// PayPalButton.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, CreditCard } from 'lucide-react';
import { useApiWithRecaptcha } from '@/utils/apiWithRecaptcha';
import { getPayPalSDKUrl, getCFWorkerUrl } from '@/config/env';

// 🚀 Remove the old interface since we're using the new env config
declare global {
  interface Window {
    paypal: any;
  }
}

interface PayPalButtonProps {
  amount: string;
  currency: string;
  videoId: string;
  onPaymentSuccess?: (response: any) => void;
  onPaymentError?: (error: any) => void;
}

export default function PayPalButton({
                                       amount,
                                       currency,
                                       videoId,
                                       onPaymentSuccess,
                                       onPaymentError,
                                     }: PayPalButtonProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSDKLoaded, setIsSDKLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // reCAPTCHA API hook
  const { callApiWithRecaptcha, isLoaded: recaptchaApiLoaded } = useApiWithRecaptcha();

  // Load PayPal SDK
  useEffect(() => {
    const loadSDK = () => {
      if (window.paypal) {
        setIsSDKLoaded(true);
        return;
      }

      const script = document.createElement('script');
      
      // 🚀 Get PayPal SDK URL from environment configuration
      try {
        const paypalUrl = getPayPalSDKUrl();
        script.src = paypalUrl;
        console.log('🔧 PayPal SDK URL:', script.src);
      } catch (error) {
        console.error('❌ Failed to get PayPal SDK URL:', error);
        setError('PayPal configuration error');
        return;
      }
      
      script.onload = () => setIsSDKLoaded(true);
      script.onerror = () => setError('Failed to load PayPal SDK');
      document.head.appendChild(script);
    };

    loadSDK();
  }, []);

  // Initialize PayPal Buttons
  useEffect(() => {
    if (!isSDKLoaded || !containerRef.current) return;

    const initializePayPal = async () => {
      try {
        setIsLoading(true);

        if (window.paypal) {
          window.paypal
              .Buttons({
                // ✅ CHỈNH 1: style để xếp dọc + full width theo thẻ cha
                style: {
                  layout: 'vertical', // vertical để 2 nút xếp dọc
                  height: 50,         // có thể chỉnh 25–55
                  tagline: false,
                },

                createOrder: async () => {
                  const amountValue = parseFloat(amount);
                  if (isNaN(amountValue) || amountValue <= 0) {
                    throw new Error(`Invalid amount: ${amount}`);
                  }

                  if (!recaptchaApiLoaded) {
                    throw new Error('reCAPTCHA not loaded yet');
                  }

                  const orderPayload = {
                    video_id: videoId,
                    amount: amountValue,
                    currency: currency,
                    shipping_preference: 'NO_SHIPPING',
                  };

                  const orderResponse = await callApiWithRecaptcha('/api/payment/create-order', {
                    method: 'POST',
                    body: JSON.stringify(orderPayload),
                  });

                  if (!orderResponse.ok) {
                    throw new Error('Failed to create payment order');
                  }

                  const orderData = await orderResponse.json();
                  return orderData.paypal_order_id;
                },

                onApprove: async (data: any) => {
                  setIsLoading(true);
                  
                  // Scroll to PayPal button to show loading state with 40px offset above
                  if (buttonRef.current) {
                    const elementTop = buttonRef.current.offsetTop;
                    window.scrollTo({
                      top: elementTop +10,
                      behavior: 'smooth'
                    });
                  }
                  
                  try {
                    // 1) Capture trên server
                    const captureResponse = await fetch(`${getCFWorkerUrl()}/api/payment/capture`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ payment_id: data.orderID }),
                    });

                    if (!captureResponse.ok) {
                      throw new Error('Payment capture failed');
                    }

                    const captureData = await captureResponse.json();
                    if (captureData.status !== 'COMPLETED') {
                      throw new Error('Payment capture not completed');
                    }

                    // 2) Bắt đầu xử lý full video
                    const processResponse = await callApiWithRecaptcha('/api/process-full-video', {
                      method: 'POST',
                      body: JSON.stringify({
                        video_id: videoId,
                        payment_id: data.orderID,
                      }),
                    });

                    if (!processResponse.ok) {
                      throw new Error('Failed to start full video processing');
                    }

                    if (onPaymentSuccess) onPaymentSuccess(captureData);
                  } catch (err) {
                    console.error(err);
                    setError('Payment capture failed. Please try again.');
                    if (onPaymentError) onPaymentError(err);
                  } finally {
                    setIsLoading(false);
                  }
                },

                onError: (err: any) => {
                  console.error('Payment failed:', err);
                  if (err?.message?.includes('Invalid amount')) {
                    setError(`Payment failed: ${err.message}. Please wait for video processing to complete.`);
                  } else {
                    setError('Payment failed. You can try again below.');
                  }
                  setIsLoading(false);
                  if (onPaymentError) onPaymentError(err);
                },

                onCancel: () => {
                  setError('Payment was cancelled. You can try again below.');
                  setIsLoading(false);
                },
              })
              .render(containerRef.current);

          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to initialize PayPal:', err);
        setError('Failed to initialize payment. Please try again.');
        setIsLoading(false);
      }
    };

    initializePayPal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSDKLoaded, amount, currency, videoId]);

  return (
      <div ref={buttonRef} className="bg-white rounded-lg md:border md:border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Secure Payment</h3>
          <div className="flex items-center space-x-2">
            <CreditCard className="w-5 h-5 text-green-600" />
            <span className="text-sm text-gray-600">SSL Secured</span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Payment Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Video Processing</span>
              <span className="font-semibold text-gray-900">
              {currency} {amount}
            </span>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Loader2 className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3" />
                  <span className="text-blue-800">Initializing payment...</span>
                </div>
              </div>
          )}

          {/* Error State */}
          {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg
                        className="w-5 h-5 text-red-600 mr-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                      <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-red-800">{error}</span>
                  </div>
                  <button
                      onClick={() => setError('')}
                      className="text-sm text-red-600 hover:text-red-800 underline"
                  >
                    Try Again
                  </button>
                </div>
              </div>
          )}

          {/* ✅ CHỈNH 2: Bọc để căn giữa + full width (tối đa 750px theo PayPal) */}
          <div className="w-full flex justify-center">
            <div
                ref={containerRef}
                className="w-full max-w-[750px] min-h-[50px] mx-auto"
                style={{ display: isSDKLoaded && !isLoading ? 'block' : 'none' }}
            />
          </div>

          {/* Security Notice */}
          <div className="text-xs text-gray-500 text-center">
            Your payment information is encrypted and secure. We never store your payment details.
          </div>
        </div>
      </div>
  );
}
